import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import blogPosts from "../data/barbequeen-blog-posts.json";
import SEO from "../components/SEO";
import { blogListPageSchema } from "../data/schemas";
import FAQSection from "../components/FAQSection";
import { blogFaqs } from "../data/faqs";

/* ─── Helpers (shared) ────────────────────────────────────────── */
const getCategory = (post) => {
  const s = (post.slug || "").toLowerCase();
  const t = (post.title || "").toLowerCase();
  if (s.includes("briyani") || s.includes("biryani") || t.includes("biryani")) return "Biryani";
  if (s.includes("grill") || s.includes("bbq") || t.includes("grill")) return "BBQ & Grill";
  if (s.includes("catering") || s.includes("event") || t.includes("catering")) return "Events";
  if (s.includes("restaurant") || s.includes("outlet") || t.includes("restaurant")) return "Dining";
  if (s.includes("chicken") || t.includes("chicken")) return "Chicken";
  return "Food & More";
};

const CATEGORY_COLORS = {
  Biryani: "#9b1c2e",
  "BBQ & Grill": "#b45309",
  Events: "#0369a1",
  Dining: "#065f46",
  Chicken: "#7c3aed",
  "Food & More": "#374151",
};

const getFeaturedImage = (post) =>
  post.featuredImage ||
  "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1200&auto=format&fit=crop";

const getReadTime = (post) =>
  Math.max(2, Math.ceil(((post.content || "").split(/\s+/).length) / 200));

const ALL_CATEGORIES = ["All", "Biryani", "BBQ & Grill", "Chicken", "Dining", "Events", "Food & More"];
const PER_PAGE = 9;

/* ─── Featured Card (hero-sized) ─────────────────────────────── */
const FeaturedCard = ({ post }) => {
  const img = getFeaturedImage(post);
  const cat = getCategory(post);
  const color = CATEGORY_COLORS[cat] || "#9b1c2e";
  const rt = getReadTime(post);

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group relative block rounded-2xl overflow-hidden shadow-lg"
      style={{ minHeight: 480 }}
    >
      <img
        src={img}
        alt={post.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        onError={(e) => {
          e.target.src = "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1200&auto=format&fit=crop";
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />

      <div className="absolute inset-0 flex flex-col justify-end p-8 sm:p-10">
        <div className="flex items-center gap-3 mb-4">
          <span
            className="text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1 rounded-full text-white"
            style={{ background: color }}
          >
            {cat}
          </span>
          <span className="text-white/50 text-[11px] font-sans">{rt} min read</span>
        </div>
        <h2 className="text-[24px] sm:text-[32px] font-bold text-white uppercase leading-tight tracking-wide mb-3 line-clamp-3 group-hover:text-[#f5c0c0] transition-colors duration-300">
          {post.title}
        </h2>
        <p className="font-sans text-[13px] text-gray-300 leading-relaxed line-clamp-2 mb-5 max-w-2xl">
          {post.excerpt}
        </p>
        <span className="inline-flex items-center gap-2 text-white text-[12px] font-bold tracking-widest uppercase group-hover:gap-3 transition-all duration-200">
          Read Article
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </span>
      </div>
    </Link>
  );
};

/* ─── Regular Blog Card ───────────────────────────────────────── */
const BlogCard = ({ post }) => {
  const img = getFeaturedImage(post);
  const cat = getCategory(post);
  const color = CATEGORY_COLORS[cat] || "#9b1c2e";
  const rt = getReadTime(post);

  return (
    <article className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col">
      {/* Image */}
      <Link to={`/blog/${post.slug}`} className="relative block h-52 overflow-hidden flex-shrink-0">
        <img
          src={img}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-108"
          loading="lazy"
          onError={(e) => {
            e.target.src = "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=600&auto=format&fit=crop";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        <span
          className="absolute top-4 left-4 text-[9px] font-bold tracking-[0.2em] uppercase px-2.5 py-1 rounded-full text-white"
          style={{ background: color }}
        >
          {cat}
        </span>
      </Link>

      {/* Content */}
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 text-[10px] text-gray-400 font-sans mb-3">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {rt} min read
        </div>

        <h3 className="text-[15px] font-bold text-gray-900 leading-snug tracking-wide uppercase line-clamp-2 mb-3 group-hover:text-[#9b1c2e] transition-colors duration-200 flex-1">
          <Link to={`/blog/${post.slug}`}>{post.title}</Link>
        </h3>

        <p className="font-sans text-[12px] text-gray-500 leading-relaxed line-clamp-2 mb-5">
          {post.excerpt || "Discover authentic flavours and culinary stories from Barbequeen Restaurant."}
        </p>

        <div className="pt-4 border-t border-gray-100">
          <Link
            to={`/blog/${post.slug}`}
            className="font-sans text-[11px] font-bold tracking-wider uppercase text-[#9b1c2e] hover:text-[#7d1624] inline-flex items-center gap-1.5 group/link"
          >
            Read More
            <svg className="w-3.5 h-3.5 group-hover/link:translate-x-1 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </article>
  );
};

/* ─── Main Page ───────────────────────────────────────────────── */
const BlogListPage = () => {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    setPage(1);
    return blogPosts.filter((p) => {
      const matchSearch =
        (p.title || "").toLowerCase().includes(search.toLowerCase()) ||
        (p.excerpt || "").toLowerCase().includes(search.toLowerCase());
      const matchCat =
        activeCategory === "All" || getCategory(p) === activeCategory;
      return matchSearch && matchCat;
    });
  }, [search, activeCategory]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return filtered.slice(start, start + PER_PAGE);
  }, [filtered, page]);

  // Featured = first post overall; grid = paged posts
  const featuredPost = blogPosts[0];
  const showFeatured = page === 1 && !search && activeCategory === "All";

  return (
    <div className="min-h-screen bg-[#f9f5f0]">
      <SEO
        title="Barbequeen Blog | Food Stories, Recipes & Offers in Salem"
        description="Read the Barbequeen blog for biryani guides, grill & barbecue tips, food stories, and offers from our restaurants in Salem, Coimbatore, Erode & Hosur today."
        keywords="Barbequeen blog, food blog Salem, biryani blog, barbecue tips, grill recipes, Barbequeen offers"
        robots="index, follow, max-image-preview:large"
        canonical="https://barbequeen.com/blog"
        og={{
          type: "website",
          url: "https://barbequeen.com/blog",
          title: "Barbequeen Blog | Food Stories, Recipes & Offers in Salem",
          description: "Biryani guides, grill & barbecue tips, food stories, and the latest offers from Barbequeen across Salem, Coimbatore, Erode & Hosur.",
          image: "https://barbequeen.com/images/blog/blog-og.jpg",
          imageWidth: "1200",
          imageHeight: "630",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN"
        }}
        schema={blogListPageSchema}
      />
      {/* ── Hero Banner ── */}
      <section className="relative overflow-hidden" style={{ minHeight: "340px" }}>
        {/* Background food image */}
        <img
          src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=1600&auto=format&fit=crop"
          alt="Barbequeen Blog"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Dark overlay — matches screenshot's deep dark-red tint */}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom, rgba(10,0,2,0.72) 0%, rgba(10,0,2,0.80) 100%)" }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-28 text-center">
          <p
            className="text-[10px] font-bold tracking-[0.45em] uppercase mb-5"
            style={{ color: "#c0424e", fontFamily: "'Inter', sans-serif" }}
          >
            From Our Kitchen
          </p>
          <h1
            className="text-4xl sm:text-6xl font-bold text-white uppercase leading-tight tracking-wide mb-5"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            The Barbequeen Blog
          </h1>
          <p
            className="text-gray-300 text-base max-w-lg mx-auto leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Stories, recipes, and culinary secrets from Salem's favourite restaurant.
          </p>
        </div>
      </section>

      {/* ── Search + Filters ── */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-4 flex flex-col sm:flex-row items-center gap-4">
          {/* Search */}
          <div className="relative flex-shrink-0 w-full sm:w-72">
            <input
              type="text"
              placeholder="Search articles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-gray-200 focus:outline-none focus:border-[#9b1c2e] font-sans text-sm shadow-inner"
            />
            <svg className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Category pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-1 scrollbar-none">
            {ALL_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setPage(1); }}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-[11px] font-bold tracking-wider uppercase transition-all duration-200 border ${
                  activeCategory === cat
                    ? "bg-[#9b1c2e] text-white border-[#9b1c2e]"
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <p className="font-sans text-[12px] text-gray-400 flex-shrink-0">
            {filtered.length} article{filtered.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {/* ── Content Area ── */}
      <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-12">

        {/* Featured Hero Post */}
        {showFeatured && (
          <div className="mb-12">
            <FeaturedCard post={featuredPost} />
          </div>
        )}

        {/* Grid */}
        {paged.length === 0 ? (
          <div className="text-center py-24 space-y-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="font-sans text-gray-500 text-sm">No articles found. Try a different search or category.</p>
            <button
              onClick={() => { setSearch(""); setActiveCategory("All"); }}
              className="font-sans text-[11px] font-bold tracking-widest uppercase text-[#9b1c2e] hover:underline"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {paged.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-14">
            <button
              onClick={() => { setPage((p) => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              disabled={page === 1}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                page === 1 ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-gray-300 text-gray-600 hover:bg-[#9b1c2e] hover:text-white hover:border-[#9b1c2e]"
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {Array.from({ length: totalPages }).map((_, i) => {
              const n = i + 1;
              if (n === 1 || n === totalPages || Math.abs(n - page) <= 1) {
                return (
                  <button
                    key={n}
                    onClick={() => { setPage(n); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className={`w-10 h-10 rounded-full text-xs font-bold transition-all ${
                      page === n ? "bg-[#9b1c2e] text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-[#9b1c2e]"
                    }`}
                  >
                    {n}
                  </button>
                );
              }
              if (n === page - 2 || n === page + 2) {
                return <span key={n} className="text-gray-400 px-1 font-sans">…</span>;
              }
              return null;
            })}

            <button
              onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              disabled={page === totalPages}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                page === totalPages ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-gray-300 text-gray-600 hover:bg-[#9b1c2e] hover:text-white hover:border-[#9b1c2e]"
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      <FAQSection faqs={blogFaqs} />
    </div>
  );
};

export default BlogListPage;
